import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { proxyAPI } from "../proxyAPI";
import TampleteIcon from "../../assets/icons/icon_save-01.svg2";
import { shortUUID } from "../helpers";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import { selectBroadcast } from "../store/slices/broadcastSlice";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import DraftIcon from "../../assets/icons/edit-01.svg2";
import PublishedIcon from "../../assets/icons/library_broadcast.svg2";
import HelpButton from "../HelpContent/HelpButton";
import MyIcon from "../Common/MyIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

export default function SaveAsTemplate({ sendList, labelClasses = "" }) {
  const values = useSelector(selectBroadcast);

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  return (
    <>
      <button type="button" className={labelClasses} onClick={handleShow}>
        <TampleteIcon width={20} />
        <span className="p-2">
          {values.Uuid ? "DUPLICATE TEMPLATE" : "SAVE AS TEMPLATE"}
        </span>
      </button>
      <Modal
        show={show}
        onHide={handleClose}
        contentClassName="bg-purple-gradient"
        size="lg"
      >
        <Modal.Header closeButton className="pb-1">
          <Modal.Title>
            <div className="p-4 pb-0 fw-bold">
              {values.Uuid ? "DUPLICATE TEMPLATE" : "SAVE AS TEMPLATE"}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-1">
          <SaveAsTemplateForm sendList={sendList} />
        </Modal.Body>
      </Modal>
    </>
  );
}

export function SaveAsTemplateForm({
  sendList,
  onSave,
  isDefaultSaveContacts = false,
}) {
  const values = useSelector(selectBroadcast);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const [label, setLabel] = useState(values.Label);
  const [description, setDescription] = useState(values.Description);
  const [reference, setReference] = useState(values.Reference);
  const [folder, setFolder] = useState(values.TagsMap?.folder || []);
  const [status, setStatus] = useState("published");
  const [isSaveContacts, setIsSaveContacts] = useState(isDefaultSaveContacts);
  const [isCreateNewSendlist, setIsCreateNewSendlist] = useState(false);

  const handleLabelChange = (e) => setLabel(e.target.value);
  const handleDescriptionChange = (e) => setDescription(e.target.value);
  const handleReferenceChange = (e) => setReference(e.target.value);
  const handleSaveContacts = () => setIsSaveContacts(!isSaveContacts);

  const save = async () => {
    setLoading(true);
    const sendlistUUID =
      values.SendList === "" || isCreateNewSendlist
        ? shortUUID()
        : values.SendList;

    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: "broadcasters",
      method: "POST",
      body: {
        ...values,
        Uuid: shortUUID(),
        Label: label,
        Description: description,
        Reference: reference,
        SendList: sendlistUUID,
        Status: status,
        ContactUuids: ["to-be-removed"],
        TagsMap: {
          ...values.TagsMap,
          folder,
        },
        Kind: "default",
      },
    });
    if (data) {
      toast.success("Template saved successfully.");
      setShow(false);
      setTimeout(() => {
        if (onSave) {
          onSave(data);
          return;
        }
        navigate(`/library/broadcast/${data.Uuid}/messages`);
      }, 100);
    }
    const sendlistResponse = await proxyAPI({
      prefix: "contacts/v1",
      method: "POST",
      url: "/upsert-send-list",
      body: {
        ...(isSaveContacts ? sendList : {}),
        uuid: sendlistUUID,
        projectUuid: "1",
      },
    });
    if (sendlistResponse) {
      toast.success("Template contacts saved successfully.");
    }

    setLoading(false);
  };

  return (
    <div className="p-4 pt-0">
      <hr className="my-4" />
      <Row className="my-3">
        <div className="fw-bold col-3">Title:</div>
        <div className="col-9">
          <Form.Control
            type="text"
            value={label}
            onChange={handleLabelChange}
          />
        </div>
      </Row>
      <Row className="my-3">
        <div className="fw-bold col-3">Reference:</div>
        <div className="col-9">
          <Form.Control
            type="text"
            value={reference}
            onChange={handleReferenceChange}
          />
        </div>
      </Row>
      <Row className="my-3">
        <div className="fw-bold col-3">Description:</div>
        <div className="col-9">
          <Form.Control
            as="textarea"
            value={description}
            onChange={handleDescriptionChange}
          />
        </div>
      </Row>
      <Row className="my-3">
        <div className="fw-bold col-3">Folder:</div>
        <div className="col-9">
          <Input
            inputType="select"
            isMulti
            isValidNewOption={(o) => o}
            value={folder}
            onChange={setFolder}
          />
        </div>
      </Row>

      <div className="row my-3">
        <div className="fw-bold col-3">Status:</div>

        <div className="col-9 row my-0">
          {[
            {
              value: "draft",
              label: "Draft",
              icon: DraftIcon,
              subText:
                "Draft broadcasts are not yet published and can be edited.",
              helpId: "broadcast-draft",
            },
            {
              value: "published",
              label: "Published",
              icon: PublishedIcon,
              subText:
                "Published broadcasts are active and can be sent. Once a broadcast is published, you can still edit it, but you should be cautious as changes may affect any scheduled sends.",
              helpId: "broadcast-published",
            },
          ].map((c) => (
            <div className="col-12 col-sm-6 m-0" key={c.value}>
              <button
                type="button"
                className={`btn w-100 h-100 bg-white p-3 pt-4 rounded-3 position-relative text-start border border-2 ${
                  status === c.value ? "border-blue" : ""
                }`}
                onClick={async () => {
                  setStatus(c.value);
                }}
              >
                <div className="position-absolute top-0 start-0">
                  <HelpButton helpKey={c.helpId} />
                </div>
                {status === c.value ? (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="position-absolute top-0 end-0 text-white bg-colored p-1"
                    style={{ borderBottomLeftRadius: 8 }}
                  />
                ) : null}
                <div className="d-flex flex-column gap-2 justify-content-center align-items-center">
                  <MyIcon width={20} Icon={c.icon} className=" me-2" />
                  <div className=" fw-bold small overflow-hidden text-center">
                    {c.label}
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="row">
        <div className="fw-bold col-3">Contacts</div>
        <div className="col-7">
          <Form.Check // prettier-ignore
            type="switch"
            id="save-contact-switch"
            className="py-0 d-flex align-items-center"
            checked={isSaveContacts}
            onChange={handleSaveContacts}
            label={<div className="ms-2 fw-bold">Save contacts</div>}
          />
        </div>
      </div>

      {isSaveContacts && values.SendList && (
        <div className="row">
          <div className="col-3"></div>
          <div className="col-7">
            <Form.Check // prettier-ignore
              type="switch"
              id="create-new-switch"
              className="py-0 d-flex align-items-center"
              checked={isCreateNewSendlist}
              onChange={() => setIsCreateNewSendlist(!isCreateNewSendlist)}
              label={<div className="ms-2 fw-bold">Create new send list</div>}
            />
            {isCreateNewSendlist ? (
              <div className="alert alert-warning col-12 my-2">
                A new send list will be created for this template. Other
                templates will not be affected.
              </div>
            ) : (
              <div className="alert alert-danger col-12 my-2">
                Your current send list will be edited. Other templates that use
                the same send list will be affected.
              </div>
            )}
          </div>
        </div>
      )}
      <hr />
      <button
        type="button"
        className="btn fw-bold  px-3 py-1 bg-colored rounded-pill  text-nowrap text-white float-end"
        onClick={save}
      >
        SAVE
      </button>
    </div>
  );
}
